<template>
  <div class="min-h-screen">
    <AppHeader />
    
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-primary-50 to-secondary-50 section-padding">
      <div class="container-custom">
        <div v-if="loading" class="text-center">
          <SkeletonLoader type="title" width="400px" class="mx-auto mb-4" />
          <SkeletonLoader type="text" width="600px" class="mx-auto" />
        </div>
        <div v-else class="text-center">
          <h1 class="font-heading text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {{ heroSection?.data?.title || 'Our Portfolio' }}
          </h1>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ heroSection?.data?.subtitle || 'Explore our latest projects and success stories.' }}
          </p>
        </div>
      </div>
    </section>
    
    <!-- Portfolio Grid -->
    <section class="section-padding bg-white">
      <div class="container-custom">
        <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <SkeletonLoader v-for="i in 6" :key="i" type="card" height="350px" />
        </div>

        <div v-else>
          <div v-if="introSection" class="text-center mb-12">
            <h2 class="font-heading text-3xl font-bold text-gray-900 mb-4">
              {{ introSection.data?.title || 'Featured Projects' }}
            </h2>
            <p class="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              {{ introSection.data?.description || 'Take a look at some of our recent work and client success stories.' }}
            </p>
          </div>

          <!-- Filters -->
          <div class="flex flex-col sm:flex-row gap-4 mb-8">
            <!-- Search -->
            <div class="flex-1">
              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search projects..."
                  class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <svg class="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
            </div>

            <!-- Category Filter -->
            <div class="flex gap-2 flex-wrap">
              <button
                @click="selectedCategory = null"
                class="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                :class="selectedCategory === null ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              >
                All
              </button>
              <button
                v-for="category in categories"
                :key="category"
                @click="selectedCategory = category"
                class="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                :class="selectedCategory === category ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              >
                {{ category }}
              </button>
            </div>
          </div>

          <!-- Portfolio Items -->
          <div v-if="filteredPortfolio.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <router-link
              v-for="project in filteredPortfolio"
              :key="project.id"
              :to="{ name: 'PortfolioDetail', params: { id: project.uid } }"
              class="card overflow-hidden group cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div class="relative h-56 bg-gradient-to-br from-primary-100 to-secondary-100 overflow-hidden">
                <img
                  v-if="project.featured_image_url"
                  :src="project.featured_image_url"
                  :alt="project.title"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div v-else class="w-full h-full flex items-center justify-center">
                  <svg class="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                </div>
                <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <!-- Featured Badge -->
                <div v-if="project.featured" class="absolute top-3 right-3">
                  <span class="flex items-center gap-1 px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full shadow-lg">
                    <svg class="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/></svg>
                    Featured
                  </span>
                </div>

                <!-- View Details Button (appears on hover) -->
                <div class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button class="px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg shadow-lg hover:bg-primary-50 transition-colors">
                    View Details
                  </button>
                </div>
              </div>

              <div class="p-6">
                <div class="flex items-center justify-between mb-3">
                  <span class="text-sm font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                    {{ project.category }}
                  </span>
                  <span v-if="project.completed_at" class="text-sm text-gray-500">
                    {{ formatYear(project.completed_at) }}
                  </span>
                </div>
                
                <h3 class="font-heading text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                  {{ project.title }}
                </h3>
                
                <p class="text-gray-600 text-sm mb-4 line-clamp-2">
                  {{ project.description }}
                </p>

                <!-- Technologies -->
                <div v-if="project.technologies && project.technologies.length > 0" class="flex flex-wrap gap-2">
                  <span
                    v-for="tech in project.technologies.slice(0, 3)"
                    :key="tech"
                    class="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded font-medium"
                  >
                    {{ tech }}
                  </span>
                  <span v-if="project.technologies.length > 3" class="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded">
                    +{{ project.technologies.length - 3 }}
                  </span>
                </div>
              </div>
            </router-link>
          </div>

          <!-- Empty State -->
          <div v-else class="text-center py-16">
            <svg class="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <h3 class="text-xl font-semibold text-gray-700 mb-2">No projects found</h3>
            <p class="text-gray-500">Try adjusting your search or filter criteria</p>
          </div>
        </div>
      </div>
    </section>
    
    <!-- CTA Section -->
    <section class="relative overflow-hidden bg-gray-50 section-padding">
      <!-- Background Elements matching Hero -->
      <div class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div class="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary-200/40 blur-3xl"></div>
        <div class="absolute -bottom-[20%] -left-[10%] w-[40%] h-[40%] rounded-full bg-secondary-200/40 blur-3xl"></div>
        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[30%] h-[30%] rounded-full bg-purple-200/30 blur-3xl"></div>
      </div>

      <div class="container-custom text-center relative z-10">
        <div v-if="loading" class="space-y-6">
          <SkeletonLoader type="title" width="400px" class="mx-auto" />
          <SkeletonLoader type="text" width="600px" class="mx-auto" />
          <SkeletonLoader type="button" class="mx-auto" />
        </div>

        <div v-else class="max-w-4xl mx-auto">
          <div class="inline-flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-primary-100 text-primary-600 text-sm font-medium mb-6 shadow-lg">
            <span class="flex h-2 w-2 rounded-full bg-primary-600 mr-2 animate-pulse"></span>
            Start Your Project
          </div>
          <h2 class="font-heading text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Ready to Start Your Project?
          </h2>
          <p class="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Let's create something amazing together.
          </p>
          <router-link to="/order-service" class="btn btn-primary text-lg px-8 py-4 shadow-xl shadow-primary-500/20 hover:shadow-primary-500/40">
            Get Started
          </router-link>
        </div>
      </div>
    </section>
    
    <AppFooter />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useContentStore } from '@/stores/content'
import { usePortfolioStore } from '@/stores/portfolio'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import SkeletonLoader from '@/components/SkeletonLoader.vue'

const contentStore = useContentStore()
const portfolioStore = usePortfolioStore()

const loading = ref(true)
const searchQuery = ref('')
const selectedCategory = ref(null)

onMounted(async () => {
  loading.value = true
  try {
    await Promise.all([
      contentStore.fetchPageSections('portfolio'),
      portfolioStore.fetchPortfolio()
    ])
  } catch (error) {
    console.error('Error loading portfolio page content:', error)
  } finally {
    loading.value = false
  }
})

const portfolioSections = computed(() => contentStore.pageSections.portfolio || [])
const heroSection = computed(() => portfolioSections.value.find(s => s.section === 'hero'))
const introSection = computed(() => portfolioSections.value.find(s => s.section === 'intro'))

const portfolioItems = computed(() => portfolioStore.portfolioItems || [])

const categories = computed(() => {
  const cats = [...new Set(portfolioItems.value.map(item => item.category))]
  return cats.filter(cat => cat).sort()
})

const filteredPortfolio = computed(() => {
  let filtered = portfolioItems.value

  // Filter by category
  if (selectedCategory.value) {
    filtered = filtered.filter(item => item.category === selectedCategory.value)
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(item =>
      item.title.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query) ||
      item.client_name.toLowerCase().includes(query) ||
      (item.technologies && item.technologies.some(tech => tech.toLowerCase().includes(query)))
    )
  }

  return filtered
})

const formatYear = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.getFullYear()
}
</script>
